/**
 * Route + site-folder helper for blog flows.
 *
 * All blog URLs follow `/<blogRoot>/<category>/<article>` (with the
 * locale prefix stripped by Nuxt i18n into `params.slug`). In
 * multi-site mode the URL can also leak the active Storyblok site
 * folder in as the first segment (preview iframe, or any URL hit
 * before the canonical 301 middleware ran) — `pathSegments` strips
 * that leak so downstream code always sees `[blog, ...]` and never
 * `[site-main, blog, ...]`.
 *
 * `scoped()` and `stripFolder()` are inverse helpers for talking to
 * the Storyblok API: scope a relative path UP into the active site
 * folder before fetching, strip the folder DOWN from a returned
 * full_slug before rendering it as a public URL.
 */
export const useBlogPath = () => {
	const route = useRoute();
	const storyblokLanguage = useStoryblokLanguage();
	const {
		folder: siteFolder,
		fallbackFolder: siteFallbackFolder,
		strict: siteStrict,
		hasLocaleBlog
	} = useStoryblokSiteFolder();
	const {
		public: { blogRoots: configuredBlogRoots }
	} = useRuntimeConfig();
	const blogRoots = Array.isArray(configuredBlogRoots)
		? (configuredBlogRoots as string[])
		: [];

	const pathSegments = computed<string[]>(() => {
		const s = route.params.slug;
		// Drop empty strings — trailing-slash URLs (`/blogg/event/`) can
		// produce a phantom `""` segment that breaks `starts_with`
		// queries (`blogg/event//` returns nothing).
		const raw = (Array.isArray(s) ? s : s ? [String(s)] : []).filter(Boolean);
		const folder = siteFolder.value;
		return folder && raw[0] === folder ? raw.slice(1) : raw;
	});

	const blogRoot = computed(() => pathSegments.value[0] ?? "");
	const categorySlug = computed(() => pathSegments.value[1] ?? "");
	const articleSlug = computed(() => pathSegments.value[2] ?? "");
	const blogPath = computed(() => `/${blogRoot.value}`);

	const scoped = (path: string): string => {
		const folder = siteFolder.value;
		return folder ? `${folder}/${path}` : path;
	};

	/**
	 * Ordered list of `starts_with` paths to try for a Storyblok query.
	 *
	 * Active locale's folder is included only when the locale owns its
	 * own blog content (`isLocaleBlog: true`, or the default locale).
	 * Locales without their own blog skip straight to the default
	 * site's folder — most sites translate articles via Storyblok i18n
	 * instead of duplicating them per folder, so the localised folder
	 * is guaranteed-empty for blog lookups.
	 *
	 *   no folder set                                     → ["blog/"]
	 *   active locale owns blog                           → ["<active>/blog/", "<default>/blog/", "blog/"]
	 *   active locale w/o own blog, has fallback folder   → ["<default>/blog/", "blog/"]
	 *   strict mode (post-migration)                      → drops the root entry
	 *
	 * Callers iterate and stop on the first non-empty result so a folder
	 * with one item isn't clobbered by a richer fallback listing.
	 */
	const folderFallbackPaths = (path: string): string[] => {
		const folder = siteFolder.value;
		const fallback = siteFallbackFolder.value;
		const localised = hasLocaleBlog.value;
		const candidates: string[] = [];
		if (folder && localised) candidates.push(`${folder}/${path}`);
		if (fallback && fallback !== folder) {
			candidates.push(`${fallback}/${path}`);
		}
		if (!siteStrict.value) candidates.push(path);
		return candidates.length ? candidates : [path];
	};

	const stripFolder = (slug: string): string => {
		let s = slug;
		// Storyblok prefixes full_slug / real_path with the request language
		// for NON-default locales (`en-GB/site-main/blog/...`). The default
		// locale's language resolves to its iso (e.g. `sv-SE`), but Storyblok
		// returns its slugs unprefixed, so the `startsWith` checks below are a
		// no-op there — no need to special-case it. Strip the prefix first so
		// the folder + depth logic sees the same shape across locales;
		// otherwise the category depth check (`parts.length === 2`) counts the
		// extra `en-GB` + folder segments and drops every category.
		const lang = storyblokLanguage.value;
		if (lang) {
			if (s.startsWith(`${lang}/`)) s = s.slice(lang.length);
			else if (s.startsWith(`/${lang}/`)) s = s.slice(lang.length + 1);
		}
		const folder = siteFolder.value;
		const fallback = siteFallbackFolder.value;
		// Strip whichever site folder is in the slug — the response may
		// have come from the active folder or the default-site fallback.
		for (const f of [folder, fallback]) {
			if (!f) continue;
			if (s.startsWith(`${f}/`)) return s.slice(f.length + 1);
			if (s.startsWith(`/${f}/`)) return s.slice(f.length + 1);
		}
		return s;
	};

	/**
	 * True when the given URL path / slug array maps to the Storyblok
	 * blog flow — its first segment matches one of the configured
	 * `blogRoots` (`NORCE_BLOG_ROOTS` env, default `blog,blogg`).
	 *
	 * Catch-all `cms/[...slug].vue` uses this BEFORE making a Storyblok
	 * request to skip the active locale's `storyblokFolder` for blog
	 * URLs when the locale has no `isLocaleBlog: true` — that scoped
	 * folder is guaranteed-empty and would just return a 404 before
	 * falling back to the default site's folder.
	 */
	const isBlogRoute = (slugInput: string | string[] | undefined): boolean => {
		if (!blogRoots.length) return false;
		const raw = (
			Array.isArray(slugInput)
				? slugInput
				: typeof slugInput === "string"
					? slugInput.split("/")
					: []
		).filter(Boolean);
		const folder = siteFolder.value;
		const stripped = folder && raw[0] === folder ? raw.slice(1) : raw;
		return blogRoots.includes(stripped[0] ?? "");
	};

	/**
	 * Folder chain for a single-story catch-all request. Mirrors
	 * `folderFallbackPaths` (active → fallback → root), but inverts the
	 * shape: returns the raw folder list expected by
	 * `fetchStoryblokContentByFolder`. Blog routes from a locale
	 * without own blog skip the active folder (`isLocaleBlog: false`),
	 * matching the savings already applied inside blog composables.
	 */
	const foldersForRoute = (
		slugInput: string | string[] | undefined
	): (string | null)[] => {
		const blog = isBlogRoute(slugInput);
		const skipActive = blog && !hasLocaleBlog.value;
		return [
			skipActive ? null : siteFolder.value,
			siteFallbackFolder.value
		].filter((f): f is string => Boolean(f));
	};

	return {
		siteFolder,
		siteFallbackFolder,
		pathSegments,
		blogRoot,
		categorySlug,
		articleSlug,
		blogPath,
		scoped,
		folderFallbackPaths,
		stripFolder,
		isBlogRoute,
		foldersForRoute
	};
};
